import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRestoreModuleMutation } from '@entities/Module';
import { paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { toast } from '@shared/ui';

interface RestoreBannerProps {
  module: ModuleSummary;
}

const RotateIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2 2v3h3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function RestoreBanner({ module: m }: RestoreBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const restoreMut = useRestoreModuleMutation();

  const handleRestore = () => {
    if (restoreMut.isPending) return;
    restoreMut.mutate(m.id, {
      onSuccess: () => {
        toast({
          tone: 'success',
          title: t('moduleDetail.restore.toastTitle'),
          message: t('moduleDetail.restore.toastBody', { name: m.title }),
        });
        navigate(paths.words, { replace: true });
      },
    });
  };

  return (
    <div className="mb-7 flex flex-col items-start gap-5 rounded-card border border-danger/25 bg-danger/[0.06] px-5 py-6 nav:flex-row nav:items-center nav:justify-between nav:gap-6 nav:px-8 nav:py-7">
      <div className="max-w-[640px]">
        <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-danger">
          {t('moduleDetail.restore.kicker')}
        </div>
        <div className="mb-2 font-serif text-[28px] font-medium leading-tight tracking-[-0.01em]">
          {t('moduleDetail.restore.title')}
        </div>
        <div className="text-[14px] leading-relaxed text-muted">
          {t('moduleDetail.restore.body')}
        </div>
        {restoreMut.isError && (
          <div className="mt-3 text-[12px] tracking-[0.02em] text-danger">
            {t('moduleDetail.restore.error')}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleRestore}
        disabled={restoreMut.isPending}
        className="inline-flex w-full shrink-0 items-center justify-center gap-2.5 rounded-pill bg-accent px-7 py-4 text-[15px] font-semibold tracking-[0.02em] text-fg-inv shadow-[0_8px_22px_-8px_rgba(243,106,28,0.55),0_2px_6px_-1px_rgba(0,0,0,0.18)] transition-transform duration-150 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 nav:w-auto"
      >
        <RotateIcon className="h-4 w-4" />
        {restoreMut.isPending ? '…' : t('moduleDetail.restore.cta')}
      </button>
    </div>
  );
}
